import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM, fmtRMSigned } from "@/lib/format";
import { Search, ArrowDownWideNarrow, ArrowUpNarrowWide, Users } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const SORTS = [
  { key: "pl", label: "cust.sort_pl" },
  { key: "outstanding", label: "cust.sort_outstanding" },
  { key: "income", label: "cust.sort_income" },
  { key: "loans", label: "cust.sort_loans" },
  { key: "name", label: "cust.sort_name" },
];

const sortValue = (c, key) => {
  if (key === "outstanding") return c.totalOutstanding;
  if (key === "income") return c.totalIncome;
  if (key === "loans") return c.loanCount;
  if (key === "name") return c.customerName.toLowerCase();
  return c.totalProfitLoss; // pl
};

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1.5 font-mono-fig text-xl font-bold ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export default function Customers() {
  const navigate = useNavigate();
  const { t: tr } = useLang();
  const [data, setData] = useState({ customers: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("pl");
  const [dir, setDir] = useState("desc");

  useEffect(() => {
    api.get("/customers/summary").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? data.customers.filter(
          (c) =>
            c.customerName.toLowerCase().includes(q) ||
            (c.customerCode || "").toLowerCase().includes(q) ||
            (c.phone || "").toLowerCase().includes(q) ||
            (c.icPassport || "").toLowerCase().includes(q)
        )
      : data.customers;
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, sort);
      const bv = sortValue(b, sort);
      const diff = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return dir === "asc" ? diff : -diff;
    });
  }, [data.customers, search, sort, dir]);

  const t = data.totals || {};

  return (
    <Layout>
      <PageHeader title={tr("cust.title")} subtitle={tr("cust.subtitle")} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat label={tr("cust.stat_customers")} value={t.customerCount ?? 0} />
        <Stat label={tr("cust.stat_disbursed")} value={fmtRM(t.totalDisbursed)} />
        <Stat label={tr("cust.stat_income")} value={fmtRM(t.totalIncome)} accent="text-green-600" />
        <Stat label={tr("cust.stat_outstanding")} value={fmtRM(t.totalOutstanding)} accent="text-amber-600" />
        <Stat label={tr("cust.stat_pl")} value={fmtRMSigned(t.totalProfitLoss)} accent={(t.totalProfitLoss ?? 0) >= 0 ? "text-green-600" : "text-red-600"} />
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="customers-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr("cust.search_ph")}
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
          />
        </div>
        <select
          data-testid="customers-sort-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-3 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 focus:ring-2 focus:ring-slate-900 outline-none cursor-pointer"
        >
          {SORTS.map((s) => <option key={s.key} value={s.key}>{tr("cust.sort")}: {tr(s.label)}</option>)}
        </select>
        <button
          data-testid="customers-sort-direction"
          onClick={() => setDir((d) => (d === "desc" ? "asc" : "desc"))}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          {dir === "desc" ? <ArrowDownWideNarrow className="h-4 w-4" /> : <ArrowUpNarrowWide className="h-4 w-4" />}
          {dir === "desc" ? tr("cust.desc") : tr("cust.asc")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3 font-medium">{tr("cust.col_customer")}</th>
              <th className="px-3 py-3 font-medium">{tr("cust.col_code")}</th>
              <th className="px-3 py-3 font-medium text-center">{tr("cust.col_loans")}</th>
              <th className="px-3 py-3 font-medium text-right">{tr("cust.col_disbursed")}</th>
              <th className="px-3 py-3 font-medium text-right">{tr("cust.col_income")}</th>
              <th className="px-3 py-3 font-medium text-right">{tr("cust.col_outstanding")}</th>
              <th className="px-3 py-3 font-medium text-right">{tr("cust.col_pl")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr
                key={c.customerName}
                data-testid={`customer-summary-row-${c.customerName}`}
                onClick={() => navigate(`/customer?q=${encodeURIComponent(c.customerName)}`)}
                className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                      <Users className="h-3.5 w-3.5 text-slate-500" />
                    </div>
                    <span className="font-medium text-slate-900">{c.customerName}</span>
                  </div>
                </td>
                <td className="px-3 py-3 font-mono-fig text-xs text-slate-500" data-testid={`customer-code-${c.customerName}`}>{c.customerCode || "—"}</td>
                <td className="px-3 py-3 text-center text-slate-600">
                  {c.loanCount}
                  <span className="ml-1.5 inline-flex gap-1 align-middle">
                    {c.activeCount > 0 && <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{c.activeCount} {tr("cust.active")}</span>}
                    {c.settledCount > 0 && <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-50 text-green-600">{c.settledCount} {tr("cust.settled")}</span>}
                  </span>
                </td>
                <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(c.totalDisbursed)}</td>
                <td className="px-3 py-3 text-right font-mono-fig text-green-600">{fmtRM(c.totalIncome)}</td>
                <td className="px-3 py-3 text-right font-mono-fig text-amber-600">{fmtRM(c.totalOutstanding)}</td>
                <td className={`px-3 py-3 text-right font-mono-fig font-semibold ${c.totalProfitLoss >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtRMSigned(c.totalProfitLoss)}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">{tr("cust.none")}</td></tr>
            )}
            {loading && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400 animate-pulse">{tr("common.loading")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
